// WindowTypesSection.jsx
// Phase 2: Window Types Section - Extracted from ProjectDetail.js

import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Trash2, Edit2, Upload, Loader2, ClipboardPaste, Image as ImageIcon } from 'lucide-react';
import SimpleWindowTypeForm from '@/components/SimpleWindowTypeForm';
import ColorPickerPopover from '@/components/ColorPickerPopover';
import { TruncatedValue } from '../shared/TruncatedValue';

export const WindowTypesSection = ({
  // Data
  windowTypes,
  selectedWindowTypeIds,
  canEditCSIItems,
  windowTypePictureUploading,
  operationLoading,
  API,
  // Dialog state
  showTypeDialog,
  editingType,
  newType,
  typePictureUrl,
  pendingWindowTypePicture,
  // Handlers
  onSelectAll,
  onConfirmBulkDelete,
  onToggleSelection,
  onDialogOpenChange,
  onNewTypeChange,
  onCreateType,
  onEditType,
  onDeleteType,
  onPictureUpload,
  onPictureDelete,
  onPasteFromClipboard,
  onPendingPictureRemove,
  onViewDrawing,
  onColorPickerOpenChange,
  onColorChange,
  onColorSave,
  canUserEdit,
}) => {
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Window Types</h2>
        <div className="flex gap-2">
          {canEditCSIItems && windowTypes.length > 0 && (
            <>
              <Button 
                variant="outline"
                onClick={onSelectAll}
              >
                {selectedWindowTypeIds.length === windowTypes.length ? 'Deselect All' : 'Select All'}
              </Button>
              <Button 
                onClick={onConfirmBulkDelete}
                disabled={selectedWindowTypeIds.length === 0}
                className="bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected ({selectedWindowTypeIds.length})
              </Button>
            </>
          )}
          {canEditCSIItems && (
            <Dialog open={showTypeDialog} onOpenChange={onDialogOpenChange}>
              <DialogTrigger asChild>
                <Button data-testid="create-window-type-button">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Window Type
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingType ? 'Edit Window Type' : 'Create Window Type'}</DialogTitle>
                  <DialogDescription>
                    {editingType ? 'Update window type specifications with imperial measurements' : 'Define specifications for a new window type with imperial measurements'}
                  </DialogDescription>
                </DialogHeader>
                <div className="mt-4">
                  <SimpleWindowTypeForm
                    formData={newType}
                    onChange={onNewTypeChange}
                    onSubmit={onCreateType}
                    isEditing={!!editingType}
                    disabled={operationLoading}
                    onPictureUpload={onPictureUpload}
                    pictureUrl={typePictureUrl || (editingType?.pictureCloudinary?.urls?.medium) || (editingType?.pictureUrl ? `${API}${editingType.pictureUrl}?t=${editingType.updatedAt || editingType.createdAt || 'default'}` : null)}
                    onPictureDelete={editingType ? () => onPictureDelete(editingType.id) : null}
                    onPasteFromClipboard={() => onPasteFromClipboard(editingType?.id || null)}
                    pendingPicture={pendingWindowTypePicture}
                    onRemovePendingPicture={onPendingPictureRemove}
                    isUploading={windowTypePictureUploading === editingType?.id}
                  />
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {windowTypes.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-slate-600">No window types created yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {windowTypes.map((type) => (
            <Card key={type.id} data-testid={`window-type-${type.id}`} className={selectedWindowTypeIds.includes(type.id) ? 'ring-2 ring-blue-500' : ''}>
              <div className="flex">
                {/* Checkbox column */}
                {canEditCSIItems && (
                  <div className="w-12 flex-shrink-0 flex items-start justify-center pt-4 border-r">
                    <Checkbox
                      checked={selectedWindowTypeIds.includes(type.id)}
                      onCheckedChange={(checked) => onToggleSelection(type.id, { target: { checked } })}
                      className="h-5 w-5"
                    />
                  </div>
                )}
                
                {/* Picture column - Left */}
                <div className="flex-shrink-0 p-4 border-r" style={{ width: '250px', height: '300px' }}>
                  {(type.pictureCloudinary?.urls?.medium || type.pictureUrl) ? (
                    <div className="h-full flex flex-col">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="font-semibold text-sm">Picture</h4>
                        <div className="flex gap-1">
                          {canUserEdit(type.createdBy) && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onPasteFromClipboard(type.id)}
                                title="Paste image from clipboard"
                                disabled={windowTypePictureUploading === type.id}
                              >
                                {windowTypePictureUploading === type.id ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <ClipboardPaste className="h-3 w-3" />
                                )}
                              </Button>
                              <label className="cursor-pointer">
                                <Button variant="outline" size="sm" asChild>
                                  <span><Upload className="h-3 w-3" /></span>
                                </Button>
                                <input
                                  type="file"
                                  className="hidden"
                                  accept="image/png,image/jpeg,image/jpg,application/pdf"
                                  onChange={(e) => onPictureUpload(e, type.id)}
                                />
                              </label>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => onPictureDelete(type.id)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                      <div
                        className="flex-1 cursor-pointer hover:opacity-80 transition-opacity flex items-center justify-center overflow-hidden"
                        onClick={() => onViewDrawing({
                          url: type.pictureCloudinary?.urls?.large || `${API}${type.pictureUrl}?t=${type.updatedAt || type.createdAt || 'default'}`,
                          title: `${type.name} - Picture`
                        })}
                      >
                        <img
                          src={type.pictureCloudinary?.urls?.large || `${API}${type.pictureUrl}?t=${type.updatedAt || type.createdAt || 'default'}`}
                          alt="Window Type Picture"
                          className="max-w-full max-h-full object-contain border rounded"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="h-full flex flex-col">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="font-semibold text-sm">Picture</h4>
                        {canUserEdit(type.createdBy) && (
                          <div className="flex gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => onPasteFromClipboard(type.id)}
                              title="Paste image from clipboard"
                              disabled={windowTypePictureUploading === type.id}
                            >
                              {windowTypePictureUploading === type.id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <ClipboardPaste className="h-3 w-3" />
                              )}
                            </Button>
                            <label className="cursor-pointer">
                              <Button variant="outline" size="sm" asChild>
                                <span><Upload className="h-3 w-3" /></span>
                              </Button>
                              <input
                                type="file"
                                className="hidden"
                                accept="image/png,image/jpeg,image/jpg,application/pdf"
                                onChange={(e) => onPictureUpload(e, type.id)}
                              />
                            </label>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 flex items-center justify-center">
                        {canUserEdit(type.createdBy) ? (
                          <label className="cursor-pointer w-full">
                            <div className="border-2 border-dashed rounded-lg p-4 text-center hover:bg-slate-50 transition-colors">
                              <Upload className="h-8 w-8 mx-auto mb-2 text-slate-400" />
                              <p className="text-sm text-slate-600">Upload Picture</p>
                              <p className="text-xs text-slate-400 mt-1">PDF, JPG, PNG</p>
                            </div>
                            <input
                              type="file"
                              className="hidden"
                              accept="image/png,image/jpeg,image/jpg,application/pdf"
                              onChange={(e) => onPictureUpload(e, type.id)}
                            />
                          </label>
                        ) : (
                          <div className="text-center text-slate-400">
                            <ImageIcon className="h-12 w-12 mx-auto mb-2" />
                            <p className="text-sm">No Picture</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Content column - Right */}
                <div className="flex-1 p-4 flex flex-col justify-between" style={{ height: '300px' }}>
                  {/* Action buttons */}
                  {canUserEdit(type.createdBy) && (
                    <div className="flex gap-2 items-center justify-end mb-2">
                      <ColorPickerPopover
                        id={`window-${type.id}`}
                        currentColor={type.markerColor}
                        pendingColor={type.pendingMarkerColor}
                        isOpen={type.colorPickerOpen}
                        isSaving={type.savingColor}
                        onOpenChange={(open) => onColorPickerOpenChange(type.id, open)}
                        onColorChange={(newColor) => onColorChange(type.id, newColor)}
                        onSave={() => onColorSave(type)}
                      />
                      <Button variant="outline" size="sm" onClick={() => onEditType(type)}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={(e) => onDeleteType(type, e)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  
                  {/* 4 Rows of content - fixed width columns */}
                  <div className="flex-1 flex flex-col justify-evenly text-sm">
                    {/* Row 1: Name, Manufacturer, Model */}
                    <div className="flex border-b pb-2">
                      <div className="font-semibold w-[95px]">Type</div>
                      <div className="w-[10px]">:</div>
                      <TruncatedValue value={type.name} className="w-[116px]" />
                      <div className="font-semibold w-[95px]">Manufacturer</div>
                      <div className="w-[10px]">:</div>
                      <TruncatedValue value={type.manufacturer} className="w-[116px]" />
                      <div className="font-semibold w-[95px]">Model</div>
                      <div className="w-[10px]">:</div>
                      <TruncatedValue value={type.model} className="flex-1" />
                    </div>
                    
                    {/* Row 2: Frame Material, Operation, U Value, CR */}
                    <div className="flex border-b pb-2">
                      <div className="font-semibold w-[95px]">Frame Material</div>
                      <div className="w-[10px]">:</div>
                      <TruncatedValue value={type.frameMaterial} className="w-[116px]" />
                      <div className="font-semibold w-[95px]">Operation</div>
                      <div className="w-[10px]">:</div>
                      <TruncatedValue value={type.operationType} className="w-[116px]" />
                      <div className="font-semibold w-[95px]">U Value</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]" title={type.uValue ? `${type.uValue} Btu/hr·ft²·°F` : 'N/A'}>{type.uValue ? `${type.uValue} Btu/hr·ft²·°F` : 'N/A'}</div>
                      <div className="font-semibold w-[95px]">CR</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{type.cr || 'N/A'}</div>
                    </div>
                    
                    {/* Row 3: Interior Finish, Exterior Finish, Hardware */}
                    <div className="flex border-b pb-2">
                      <div className="font-semibold w-[95px]">Interior Finish</div>
                      <div className="w-[10px]">:</div>
                      <TruncatedValue value={type.finishInterior} className="w-[116px]" />
                      <div className="font-semibold w-[95px]">Exterior Finish</div>
                      <div className="w-[10px]">:</div>
                      <TruncatedValue value={type.finishExterior} className="w-[116px]" />
                      <div className="font-semibold w-[95px]">Hardware</div>
                      <div className="w-[10px]">:</div>
                      <TruncatedValue value={type.hardware} className="flex-1" />
                    </div>
                    
                    {/* Row 4: Warranty Periods */}
                    <div className="flex">
                      <div className="font-semibold w-[95px]">Frame Warranty</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{type.frameWarrantyYears ? `${type.frameWarrantyYears} Years` : 'N/A'}</div>
                      <div className="font-semibold w-[95px]">Install Warranty</div>
                      <div className="w-[10px]">:</div>
                      <div className="flex-1">{type.installationWarrantyYears ? `${type.installationWarrantyYears} Years` : 'N/A'}</div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default WindowTypesSection;
